import { useMemo, useState } from "react";
import { Archive, Database, History, ListChecks, Settings2, Users } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table.js";
import { Badge } from "../../../components/ui/badge.js";
import { Button } from "../../../components/ui/button.js";
import { Progress } from "../../../components/ui/progress.js";
import { Pagination } from "../../../components/ui/pagination.js";
import { SearchInput } from "../../../components/ui/search-input.js";
import { Input } from "../../../components/ui/input.js";
import { Label } from "../../../components/ui/label.js";
import { RangeSlider } from "../../../components/ui/range-slider.js";
import { SelectField } from "../../../components/ui/select-field.js";
import { breakdown, progress, type HrmsDb, type RunRecord, type SimWorld } from "../../../lib/migration-sim/state.js";
import type { SimEmployee, SourceSettings } from "../../../lib/migration-sim/source.js";

const OUTCOME_TONE: Record<RunRecord["outcome"], "success" | "warning" | "error" | "info"> = {
  done: "success", "done-with-errors": "warning", crashed: "error", running: "info",
};

function verifyTone(run: RunRecord): { tone: "success" | "warning" | "neutral"; text: string } {
  const s = run.verify?.summary;
  if (!s) return { tone: "neutral", text: "not verified" };
  const bad = s.leave.mismatch + s.attendance.mismatch + (s.employees?.missing.length ?? 0);
  return bad ? { tone: "warning", text: `${bad} mismatches` } : { tone: "success", text: "all match" };
}

// ── Runs ─────────────────────────────────────────────────────────────────────

export function RunsPanel({ world, run, busy }: { world: SimWorld; run: (cmd: string) => void; busy: boolean }) {
  const runs = [...world.memory.runs].reverse();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><History className="h-4 w-4" /> Run history</CardTitle>
        <CardDescription>Every migrate:team run in the memory, newest first. Each row's buttons run that batch's next command in the console.</CardDescription>
      </CardHeader>
      <CardContent>
        {runs.length === 0 ? (
          <p className="text-sm text-surface-500">No runs yet. Pick employees on the left and run preview, then migrate:team.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Run</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Outcome</TableHead>
                <TableHead>Filters</TableHead>
                <TableHead className="text-right">Picked</TableHead>
                <TableHead className="text-right">Loaded</TableHead>
                <TableHead className="text-right">Failed</TableHead>
                <TableHead className="text-right">Progress after</TableHead>
                <TableHead>Modules</TableHead>
                <TableHead>Verify</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {runs.map((r) => {
                const v = verifyTone(r);
                return (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono font-semibold">{r.id}<div className="font-sans text-[11px] font-normal text-surface-500">{r.startedAt.slice(0, 16).replace("T", " ")}</div></TableCell>
                    <TableCell><Badge tone={r.dryRun ? "accent" : "info"}>{r.dryRun ? "DRY" : "REAL"}</Badge></TableCell>
                    <TableCell><Badge tone={OUTCOME_TONE[r.outcome]}>{r.outcome}</Badge></TableCell>
                    <TableCell className="max-w-[260px] font-mono text-[11px] text-surface-600" title={r.filterText}>{r.filterText}</TableCell>
                    <TableCell className="text-right">{r.bayIds.length}</TableCell>
                    <TableCell className="text-right">{r.loaded}</TableCell>
                    <TableCell className="text-right">{r.failed.length || "—"}</TableCell>
                    <TableCell className="text-right">{r.progressAfter ? `${r.progressAfter.percent}%` : "—"}</TableCell>
                    <TableCell className="text-[11px] text-surface-600">{r.modules?.length ? r.modules.map((m) => `${m.module}${m.dryRun ? "(dry)" : ""} ${m.written}/${m.read}`).join(", ") : "—"}</TableCell>
                    <TableCell><Badge tone={v.tone}>{v.text}</Badge></TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button size="xs" variant="secondary" disabled={busy} onClick={() => run(`pnpm --filter @hrms/migration-v2 show ${r.id}`)}>Show</Button>
                        <Button size="xs" variant="secondary" disabled={busy} onClick={() => run(`pnpm --filter @hrms/migration-v2 migrate:modules --batch=${r.id}${r.dryRun ? " --dry-run" : ""}`)}>Modules</Button>
                        <Button size="xs" variant="secondary" disabled={busy} onClick={() => run(`pnpm --filter @hrms/migration-v2 verify --batch=${r.id}`)}>Verify</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ── Progress ─────────────────────────────────────────────────────────────────

export function ProgressPanel({ world }: { world: SimWorld }) {
  const p = progress(world.memory);
  const section = (title: string, by: "status" | "department") => (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold text-surface-800">By {title}</h3>
      {breakdown(world.memory, by).map((b) => (
        <div key={b.key} className="grid grid-cols-[minmax(0,180px)_1fr_auto] items-center gap-3 text-sm">
          <span className="truncate text-surface-700" title={b.key}>{b.key}</span>
          <Progress value={b.percent} />
          <span className="w-24 text-right tabular-nums text-surface-600">{b.migrated} / {b.total} · {b.percent}%</span>
        </div>
      ))}
    </div>
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ListChecks className="h-4 w-4" /> Migration progress</CardTitle>
        <CardDescription>Migrated (by real runs) out of everyone in the source. Dry runs never count.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {!world.memory.source ? (
          <p className="text-sm text-surface-500">No run yet: the source total is counted by the first preview or migrate:team run.</p>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between">
                <span className="text-2xl font-bold text-surface-900">{p.percent}%</span>
                <span className="text-sm text-surface-600">{p.migrated} of {p.total} employees</span>
              </div>
              <Progress value={p.percent} className="h-3" />
            </div>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {section("status", "status")}
              {section("department", "department")}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Source data ──────────────────────────────────────────────────────────────

export function SourcePanel({ world, source, picked }: { world: SimWorld; source: SimEmployee[]; picked: Set<number> }) {
  const [q, setQ] = useState("");
  const [show, setShow] = useState<"all" | "picked" | "migrated" | "remaining">("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const byBay = useMemo(() => new Map(source.map((m) => [m.legacyBayUserId, m])), [source]);
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return source.filter((m) => {
      const done = !!world.memory.employees[String(m.legacyBayUserId)];
      if (show === "picked" && !picked.has(m.legacyBayUserId)) return false;
      if (show === "migrated" && !done) return false;
      if (show === "remaining" && done) return false;
      return !needle || [m.employeeCode, m.firstName, m.lastName, m.email, m.departmentName, m.designation, String(m.legacyBayUserId)].some((v) => v.toLowerCase().includes(needle));
    });
  }, [source, world.memory.employees, picked, q, show]);
  const totalPages = Math.max(1, Math.ceil(rows.length / limit));
  const current = Math.min(page, totalPages);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Users className="h-4 w-4" /> Legacy source (synthetic)</CardTitle>
        <CardDescription>The employees the simulated tool reads after dedupe and HR's report. Generated from a seed; no real person is in this list.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <SearchInput placeholder="Search code, name, email, department, legacy id" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="w-80" />
          <SelectField value={show} onValueChange={(v) => { setShow(v as typeof show); setPage(1); }} className="w-56">
            <option value="all">Everyone ({source.length})</option>
            <option value="picked">Picked by current filters ({picked.size})</option>
            <option value="migrated">Migrated</option>
            <option value="remaining">Not migrated yet</option>
          </SelectField>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Bay id</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Office</TableHead>
              <TableHead>Hired</TableHead>
              <TableHead>Manager</TableHead>
              <TableHead>Migration</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.slice((current - 1) * limit, current * limit).map((m) => {
              const rec = world.memory.employees[String(m.legacyBayUserId)];
              const mgr = m.managerBayId ? byBay.get(m.managerBayId) : undefined;
              return (
                <TableRow key={m.legacyBayUserId}>
                  <TableCell className="font-mono text-xs">{m.employeeCode}</TableCell>
                  <TableCell className="font-mono text-xs text-surface-500">{m.legacyBayUserId}</TableCell>
                  <TableCell>{m.firstName} {m.lastName}<div className="text-[11px] text-surface-500">{m.email}</div></TableCell>
                  <TableCell><Badge tone={m.status === "TERMINATED" ? "neutral" : m.status === "ACTIVE" ? "success" : "warning"}>{m.status}</Badge></TableCell>
                  <TableCell className="text-xs">{m.role}</TableCell>
                  <TableCell className="text-xs">{m.departmentName}</TableCell>
                  <TableCell className="text-xs">{m.officeId}</TableCell>
                  <TableCell className="text-xs tabular-nums">{m.hireDate}</TableCell>
                  <TableCell className="font-mono text-xs text-surface-500">{mgr ? mgr.employeeCode : "—"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {rec && <Badge tone="success">{rec.runId}</Badge>}
                      {picked.has(m.legacyBayUserId) && <Badge tone="info">picked</Badge>}
                      {m.failsToLoad && <Badge tone="error" title={m.failsToLoad}>will fail</Badge>}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Pagination meta={{ total: rows.length, page: current, limit, totalPages }} onPageChange={setPage} onPageSizeChange={(l) => { setLimit(l); setPage(1); }} />
      </CardContent>
    </Card>
  );
}

// ── Simulated HRMS ───────────────────────────────────────────────────────────

export function HrmsPanel({ hrms, source, run, busy }: { hrms: HrmsDb; source: SimEmployee[]; run: (cmd: string) => void; busy: boolean }) {
  const emps = Object.values(hrms.employees);
  const sum = (r: Record<string, number>) => Object.values(r).reduce((s, n) => s + n, 0);
  const count = (r: Record<string, unknown>) => Object.keys(r).length;
  const rows: Array<[string, number, string]> = [
    ["Employees + logins", emps.length, `of ${source.length} in the source`],
    ["Reporting manager set", emps.filter((e) => e.managerSet).length, "the rest wait for their manager's batch"],
    ["Personal details", count(hrms.personal), "personal module"],
    ["Termination records", count(hrms.terminations), "termination module"],
    ["Leave requests", sum(hrms.leaves), "ids legacy-leave-<id>, so re-runs update in place"],
    ["Attendance days", sum(hrms.attendance), "one per employee per day"],
    ["Salary records", count(hrms.compensation), "compensation module"],
    ["Documents", sum(hrms.documents), "docs import (--docs-dir)"],
    ["Holidays", hrms.holidays, "company-wide, never batched"],
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Database className="h-4 w-4" /> HRMS database (simulated)</CardTitle>
        <CardDescription>What the runs wrote. reset only forgets the run memory and leaves these rows; reset:db (or migrate:team --reset) truncates employees and everything hanging off them.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Table</TableHead><TableHead className="text-right">Rows</TableHead><TableHead>Note</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(([label, n, note]) => (
              <TableRow key={label}>
                <TableCell className="font-medium">{label}</TableCell>
                <TableCell className="text-right tabular-nums">{n.toLocaleString()}</TableCell>
                <TableCell className="text-xs text-surface-500">{note}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div>
          <Button variant="secondary" size="sm" disabled={busy} onClick={() => run("pnpm --filter @hrms/migration-v2 reset:db")}>Run reset:db</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Memory file ──────────────────────────────────────────────────────────────

export function MemoryPanel({ world }: { world: SimWorld }) {
  const [open, setOpen] = useState<string | null>(null);
  const shown = open ? world.archives.find((a) => a.file === open)?.state : world.memory;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Archive className="h-4 w-4" /> Run memory</CardTitle>
        <CardDescription>state/migration-state.json as the tool writes it, and the archives reset leaves behind.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <SelectField value={open ?? ""} onValueChange={(v) => setOpen(v || null)} className="w-full sm:w-auto">
          <option value="">state/migration-state.json (current)</option>
          {world.archives.map((a) => <option key={a.file} value={a.file}>{a.file.replace("tools/migration-v2/", "")}</option>)}
        </SelectField>
        <pre className="max-h-[420px] overflow-auto rounded-lg border border-surface-200 bg-surface-50 p-3 font-mono text-[11px] leading-4 text-surface-700">
          {JSON.stringify(shown, null, 2)}
        </pre>
      </CardContent>
    </Card>
  );
}

// ── Simulator settings ───────────────────────────────────────────────────────

export type Speed = "instant" | "normal" | "slow";

export function SettingsPanel({ settings, speed, onSpeed, onApply, onWipe, busy }: {
  settings: SourceSettings; speed: Speed; onSpeed: (s: Speed) => void;
  onApply: (s: SourceSettings) => void; onWipe: () => void; busy: boolean;
}) {
  const [draft, setDraft] = useState(settings);
  const changed = draft.size !== settings.size || draft.seed !== settings.seed || draft.failureRate !== settings.failureRate;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Settings2 className="h-4 w-4" /> Simulator settings</CardTitle>
        <CardDescription>Shape the pretend legacy source to try cases: a bigger company, a different mix, or more rows that fail to load.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="flex flex-col gap-1">
            <Label>Employees in the source</Label>
            <Input type="number" min={10} max={3000} value={draft.size} onChange={(e) => setDraft({ ...draft, size: Math.min(3000, Math.max(10, Number(e.target.value) || 10)) })} />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Data seed</Label>
            <Input type="number" value={draft.seed} onChange={(e) => setDraft({ ...draft, seed: Number(e.target.value) || 0 })} />
          </div>
          <div className="flex flex-col gap-1">
            <Label>Run speed</Label>
            <SelectField value={speed} onValueChange={(v) => onSpeed(v as Speed)}>
              <option value="instant">Instant</option>
              <option value="normal">Normal (demo)</option>
              <option value="slow">Slow (presenting)</option>
            </SelectField>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <Label>Rows that fail to load</Label>
            <span className="text-sm font-semibold">{draft.failureRate}%</span>
          </div>
          <RangeSlider min={0} max={30} value={draft.failureRate} onValueChange={(v) => setDraft({ ...draft, failureRate: v })} />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button disabled={!changed || busy} onClick={() => onApply(draft)}>Apply and start over</Button>
          <Button variant="destructive" disabled={busy} onClick={onWipe}>Wipe the simulator</Button>
        </div>
        <p className="text-xs text-surface-500">Changing the source starts the simulator over (memory, archives and the pretend HRMS), since the old runs point at people who no longer exist.</p>
      </CardContent>
    </Card>
  );
}
